import { apiClient } from './client';

/**
 * RTMP edge appliances + their cameras (VIDEO-STREAMING).
 *
 * An appliance is the on-site box that receives camera RTMP and records in
 * sync with a capture session. A tenant admin registers it (mints an
 * enrollment code the edge-agent redeems), watches it come online, and
 * registers the cameras it ingests.
 */

export type ApplianceStatus = 'active' | 'suspended' | 'retired';

export interface Appliance {
  id: string;
  label: string;
  status: ApplianceStatus;
  make: string | null;
  model: string | null;
  os_name: string | null;
  os_version: string | null;
  agent_version: string | null;
  last_seen_at: string | null;
  enrolled_at: string;
  /** OS capture devices the agent detected (ffmpeg), for the camera picker. */
  detected_cameras: DetectedCamera[] | null;
  /** Why the agent found no network camera, when it found none. Null on an
   *  agent older than 0.3.14 — "not reported", not "nothing there". */
  camera_discovery: CameraDiscovery | null;
  /** Clips recorded but not yet uploaded. Null when nothing is waiting. */
  pending_clips: PendingClips | null;
  /** Base URL a same-LAN browser uses to watch a camera live (MediaMTX). */
  webrtc_base: string | null;
}

export type CameraDiscoveryState =
  | 'camera_found'
  | 'no_camera_network'
  | 'camera_network_only'
  | 'no_wifi_info';

export interface CameraDiscovery {
  state: CameraDiscoveryState;
  /** SSID prefix the agent looked for ("EP" for the ORDRO EP-series). */
  ssid_prefix: string;
  /** The networks each adapter is joined to, as "iface=SSID". */
  ssids?: string[];
  checked_at: string;
}

export interface PendingClips {
  count: number;
  /** ISO timestamp of the oldest waiting clip. */
  oldest_at?: string;
}

export interface DetectedCamera {
  device_path: string;
  label: string;
  /** Capture resolutions this camera supports at ~30fps, largest first. */
  modes?: string[] | null;
}

export interface EnrollmentToken {
  /** Raw 8-character code, returned ONCE — hand it to the edge-agent. */
  code: string;
  expires_at: string;
}

export type CameraConnection = 'wifi' | 'usb' | 'builtin' | 'rtsp';
export type CameraType = 'gopro' | 'ordro' | 'generic';

export interface Camera {
  id: string;
  appliance_id: string;
  name: string;
  camera_type: CameraType;
  connection: CameraConnection;
  stream_key: string | null;
  device_path: string | null;
  capture_size: string | null;
  location_id: string | null;
  status: 'active' | 'retired';
}

export interface CameraCreate {
  appliance_id: string;
  name: string;
  camera_type: CameraType;
  connection: CameraConnection;
  stream_key?: string | null;
  device_path?: string | null;
  capture_size?: string | null;
  location_id?: string | null;
}

export async function listAppliances(tenantId: string): Promise<Appliance[]> {
  const { data } = await apiClient.get<Appliance[]>(
    `/api/v1/tenants/${tenantId}/appliances`,
  );
  return data;
}

export async function issueApplianceCode(
  tenantId: string,
): Promise<EnrollmentToken> {
  const { data } = await apiClient.post<EnrollmentToken>(
    `/api/v1/tenants/${tenantId}/appliances/enrollments`,
    {},
  );
  return data;
}

export async function retireAppliance(
  tenantId: string,
  applianceId: string,
): Promise<void> {
  await apiClient.delete(
    `/api/v1/tenants/${tenantId}/appliances/${applianceId}`,
  );
}

/** Rename an appliance (e.g. "OR-3 Cart") so operators can tell boxes apart. */
export async function renameAppliance(
  tenantId: string,
  applianceId: string,
  label: string,
): Promise<Appliance> {
  const { data } = await apiClient.patch<Appliance>(
    `/api/v1/tenants/${tenantId}/appliances/${applianceId}`,
    { label },
  );
  return data;
}

export async function listCameras(tenantId: string): Promise<Camera[]> {
  const { data } = await apiClient.get<Camera[]>(
    `/api/v1/tenants/${tenantId}/cameras`,
  );
  return data;
}

export async function createCamera(
  tenantId: string,
  body: CameraCreate,
): Promise<Camera> {
  const { data } = await apiClient.post<Camera>(
    `/api/v1/tenants/${tenantId}/cameras`,
    body,
  );
  return data;
}

export async function retireCamera(
  tenantId: string,
  cameraId: string,
): Promise<void> {
  await apiClient.delete(`/api/v1/tenants/${tenantId}/cameras/${cameraId}`);
}

/** Update an existing camera in place (currently just its capture resolution). */
export async function updateCamera(
  tenantId: string,
  cameraId: string,
  body: { capture_size?: string | null },
): Promise<Camera> {
  const { data } = await apiClient.patch<Camera>(
    `/api/v1/tenants/${tenantId}/cameras/${cameraId}`,
    body,
  );
  return data;
}

/** Ask the agent to grab a test frame from this camera. */
export async function requestSnapshot(
  tenantId: string,
  cameraId: string,
): Promise<void> {
  await apiClient.post(
    `/api/v1/tenants/${tenantId}/cameras/${cameraId}/snapshot`,
  );
}

// Live check is now local-only — the portal polls the appliance's localhost
// preview server directly (no backend/S3 relay), so there's no requestLiveCheck
// API. The single test snapshot (getSnapshot, camera-list thumbnail) stays.

export interface Snapshot {
  url: string | null;
  taken_at: string | null;
}

/** Latest test snapshot (presigned URL + when it was taken). */
export async function getSnapshot(
  tenantId: string,
  cameraId: string,
): Promise<Snapshot> {
  const { data } = await apiClient.get<Snapshot>(
    `/api/v1/tenants/${tenantId}/cameras/${cameraId}/snapshot`,
  );
  return data;
}

/** Which per-target edge installers are published (so we don't offer a download
 *  that would 404 at S3). Keyed by os-arch, e.g. 'windows-amd64' | 'linux-arm64'
 *  | 'macos-arm64'. */
export async function installerStatus(): Promise<Record<string, boolean>> {
  const { data } = await apiClient.get<Record<string, boolean>>(
    '/api/v1/appliances/installer/status',
  );
  return data;
}

/** The newest published edge agent (EDGE-SELF-UPDATE). All null until a
 *  release with a manifest is published. */
export interface AgentRelease {
  latest_version: string | null;
  published_at: string | null;
  /** Set when the release needs the whole bundle replaced by hand. */
  manual_reason: string | null;
}

export async function agentRelease(): Promise<AgentRelease> {
  const { data } = await apiClient.get<AgentRelease>('/api/v1/appliances/agent-release');
  return data;
}

/** Dotted numeric compare: '0.3.25' is newer than '0.3.9'. */
export function isNewerVersion(a: string, b: string): boolean {
  const pa = a.split('.').map((x) => Number.parseInt(x, 10) || 0);
  const pb = b.split('.').map((x) => Number.parseInt(x, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const x = pa[i] ?? 0;
    const y = pb[i] ?? 0;
    if (x !== y) return x > y;
  }
  return false;
}

/** The first agent that updates itself; older boxes need one manual update. */
export const FIRST_SELF_UPDATING_AGENT = '0.3.25';

/**
 * True when an appliance has heartbeated recently enough to be "online".
 * The agent posts a heartbeat every ~30 s; allow 90 s of slack.
 */
export function isApplianceOnline(a: Appliance): boolean {
  if (!a.last_seen_at) return false;
  return Date.now() - new Date(a.last_seen_at).getTime() < 90_000;
}

/** MediaMTX/WebRTC path for a camera — must match the agent's `CameraPath`. */
export function cameraStreamPath(c: Camera): string {
  const clean = (s: string) => (s || '').replace(/[^a-zA-Z0-9_-]/g, '');
  return c.connection === 'wifi'
    ? clean(c.stream_key ?? '')
    : `cam_${clean(c.id)}`;
}

/** Just the offer bits we need to format trickle-ICE candidate fragments. */
interface OfferData {
  iceUfrag: string;
  icePwd: string;
  medias: string[];
}

function parseOffer(sdp: string): OfferData {
  const od: OfferData = { iceUfrag: '', icePwd: '', medias: [] };
  for (const line of sdp.split('\r\n')) {
    if (line.startsWith('m=')) od.medias.push(line.slice(2));
    else if (!od.iceUfrag && line.startsWith('a=ice-ufrag:'))
      od.iceUfrag = line.slice('a=ice-ufrag:'.length);
    else if (!od.icePwd && line.startsWith('a=ice-pwd:'))
      od.icePwd = line.slice('a=ice-pwd:'.length);
  }
  return od;
}

/** Format ICE candidates as the `application/trickle-ice-sdpfrag` body WHEP's
 *  PATCH expects (same shape MediaMTX's own reader sends). */
function sdpFragment(od: OfferData, candidates: RTCIceCandidate[]): string {
  const byMedia: Record<number, RTCIceCandidate[]> = {};
  for (const c of candidates) {
    const mid = c.sdpMLineIndex ?? 0;
    (byMedia[mid] ??= []).push(c);
  }
  let frag = `a=ice-ufrag:${od.iceUfrag}\r\na=ice-pwd:${od.icePwd}\r\n`;
  od.medias.forEach((media, mid) => {
    if (byMedia[mid]) {
      frag += `m=${media}\r\na=mid:${mid}\r\n`;
      for (const c of byMedia[mid]) frag += `a=${c.candidate}\r\n`;
    }
  });
  return frag;
}

/**
 * Play a MediaMTX WebRTC stream into a <video> via WHEP, using **trickle ICE**
 * — POST the offer immediately, read the `Location` session URL, then PATCH
 * each local ICE candidate to it. This mirrors MediaMTX's own reader exactly;
 * MediaMTX does NOT read candidates from the offer body, so a non-trickle
 * "gather fully then POST" offer connects to nothing and the <video> stays
 * black. Returns the RTCPeerConnection so the caller can close it.
 */
export interface WhepSession {
  pc: RTCPeerConnection;
  /** Tear the session down: DELETE the WHEP session so MediaMTX drops the
   *  reader immediately (releasing the camera), then close the peer. */
  stop: () => void;
}

export async function playWhep(
  video: HTMLVideoElement,
  whepUrl: string,
): Promise<WhepSession> {
  const pc = new RTCPeerConnection();
  pc.addTransceiver('video', { direction: 'recvonly' });
  pc.addTransceiver('audio', { direction: 'recvonly' });
  // A local data channel is what makes onicecandidate fire in this flow.
  pc.createDataChannel('');

  // Own the MediaStream so the track attaches even if the answer omits msid.
  const stream = new MediaStream();
  video.srcObject = stream;
  pc.ontrack = (e) => {
    stream.addTrack(e.track);
    void video.play().catch(() => {});
  };

  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);
  const offerData = parseOffer(offer.sdp ?? '');

  // Candidates that fire before we know the session URL wait in this queue.
  let sessionUrl: string | null = null;
  const queued: RTCIceCandidate[] = [];
  const patch = (cands: RTCIceCandidate[]) => {
    if (!sessionUrl || cands.length === 0) return;
    void fetch(sessionUrl, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/trickle-ice-sdpfrag',
        'If-Match': '*',
      },
      body: sdpFragment(offerData, cands),
    }).catch(() => {});
  };
  pc.onicecandidate = (e) => {
    if (!e.candidate) return;
    if (sessionUrl) patch([e.candidate]);
    else queued.push(e.candidate);
  };

  let res: Response;
  try {
    res = await fetch(whepUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/sdp' },
      body: offer.sdp ?? '',
    });
  } catch (e) {
    pc.close();
    // A rejected fetch here is almost always CSP connect-src or a network
    // block — surface it instead of hanging silently.
    throw new Error(
      `Could not reach ${whepUrl} (${e instanceof Error ? e.message : 'network error'}) — the appliance must be on this network`,
    );
  }
  if (res.status !== 201) {
    pc.close();
    const detail = await res.text().catch(() => '');
    throw new Error(
      `WHEP handshake failed (HTTP ${res.status})${detail ? ` — ${detail}` : ''}`,
    );
  }
  // The session URL (where we PATCH candidates) comes from the Location header,
  // resolved against the WHEP URL since it's usually relative.
  const loc = res.headers.get('location');
  sessionUrl = loc ? new URL(loc, whepUrl).toString() : whepUrl;
  await pc.setRemoteDescription({ type: 'answer', sdp: await res.text() });
  patch(queued.splice(0));

  let stopped = false;
  const stop = () => {
    if (stopped) return;
    stopped = true;
    // WHEP session teardown — without this MediaMTX only notices the reader
    // left when ICE times out, so the runOnDemand ffmpeg (and the camera light)
    // lingers. keepalive lets it fire even if the tab is closing.
    if (sessionUrl) {
      void fetch(sessionUrl, { method: 'DELETE', keepalive: true }).catch(
        () => {},
      );
    }
    pc.close();
  };
  return { pc, stop };
}
